import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Megaphone, Copy, Check, ArrowRight, ShieldQuestion } from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import Button from '../../../components/ui/Button';
import whsService from '../../../services/whs';
import type {
  CreateReportPayload, CreateReportResult, ReportType, ReportSeverity, ReportTreatment, FarmOption,
} from '../../../types/whs';
import { REPORT_TYPE_LABEL, SEVERITY_LABEL, TREATMENT_LABEL } from '../../../components/whs/whsConstants';

const REPORT_TYPES = Object.keys(REPORT_TYPE_LABEL) as ReportType[];
const SEVERITIES = Object.keys(SEVERITY_LABEL) as ReportSeverity[];
const TREATMENTS = Object.keys(TREATMENT_LABEL) as ReportTreatment[];

interface FormState {
  report_type: ReportType;
  title: string;
  description: string;
  occurred_at: string; // datetime-local value
  location: string;
  farm_id: string; // select value; '' = none
  severity: ReportSeverity;
  injury_type: string;
  body_part: string;
  treatment: ReportTreatment;
  immediate_action: string;
  is_notifiable: boolean;
  is_anonymous: boolean;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
}

const EMPTY_FORM: FormState = {
  report_type: 'hazard',
  title: '',
  description: '',
  occurred_at: '',
  location: '',
  farm_id: '',
  severity: 'minor',
  injury_type: '',
  body_part: '',
  treatment: 'none',
  immediate_action: '',
  is_notifiable: false,
  is_anonymous: false,
  contact_name: '',
  contact_email: '',
  contact_phone: '',
};

const inputCls =
  'w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none';
const labelCls = 'block text-sm font-medium text-neutral-700 mb-1';

export default function WhsReport() {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [farms, setFarms] = useState<FarmOption[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateReportResult | null>(null);
  const [copied, setCopied] = useState(false);

  const loadOptions = useCallback(async () => {
    const res = await whsService.getReportOptions();
    if (res.success) setFarms(res.data.farms);
  }, []);

  useEffect(() => { loadOptions(); }, [loadOptions]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.report_type || !form.title.trim() || !form.description.trim()) {
      setError('Please provide a report type, a title and a description.');
      return;
    }
    setSubmitting(true);
    const payload: CreateReportPayload = {
      report_type: form.report_type,
      title: form.title.trim(),
      description: form.description.trim(),
      occurred_at: form.occurred_at ? new Date(form.occurred_at).toISOString() : null,
      location: form.location.trim() || null,
      farm_id: form.farm_id ? Number(form.farm_id) : null,
      severity: form.severity,
      injury_type: form.injury_type.trim() || null,
      body_part: form.body_part.trim() || null,
      treatment: form.treatment,
      immediate_action: form.immediate_action.trim() || null,
      is_notifiable: form.is_notifiable,
      is_anonymous: form.is_anonymous,
      contact_name: form.is_anonymous ? null : form.contact_name.trim() || null,
      contact_email: form.is_anonymous ? null : form.contact_email.trim() || null,
      contact_phone: form.is_anonymous ? null : form.contact_phone.trim() || null,
    };
    const res = await whsService.submitReport(payload);
    if (res.success) setResult(res.data);
    else setError(res.error.message);
    setSubmitting(false);
  };

  const handleCopy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.tracking_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy to clipboard — please copy the code manually.');
    }
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setResult(null);
    setError(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Report a safety issue"
        subtitle="Any worker can report a hazard, incident or near miss — no login barriers, no blame"
        icon={<Megaphone className="h-6 w-6 text-white" />}
      />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">{error}</div>
        )}

        {result ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
              <Check className="h-6 w-6 text-emerald-700" />
            </div>
            <h2 className="text-lg font-semibold text-emerald-900">Thank you — your report was submitted</h2>
            <p className="mt-1 text-sm text-emerald-800">
              Keep this tracking code somewhere safe. You can use it to follow up on progress at any time.
            </p>
            <div className="mt-5 inline-flex items-center gap-3 rounded-lg bg-white border border-emerald-300 px-5 py-4">
              <span className="font-mono text-2xl font-bold tracking-widest text-neutral-900">
                {result.tracking_code}
              </span>
              <button
                type="button"
                onClick={handleCopy}
                className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
              >
                {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                {copied ? 'Copied' : 'Copy code'}
              </button>
            </div>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Button onClick={() => navigate('/whs/track')}>
                Track this report <ArrowRight className="h-4 w-4" />
              </Button>
              <Button variant="secondary" onClick={resetForm}>Submit another report</Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="rounded-xl border border-neutral-200 bg-white p-5 space-y-4">
              <div>
                <label className={labelCls} htmlFor="report_type">Report type <span className="text-red-500">*</span></label>
                <select
                  id="report_type"
                  className={inputCls}
                  value={form.report_type}
                  onChange={(e) => set('report_type', e.target.value as ReportType)}
                >
                  {REPORT_TYPES.map((t) => (
                    <option key={t} value={t}>{REPORT_TYPE_LABEL[t]}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelCls} htmlFor="title">Title <span className="text-red-500">*</span></label>
                <input
                  id="title"
                  className={inputCls}
                  value={form.title}
                  onChange={(e) => set('title', e.target.value)}
                  placeholder="Short summary of what happened"
                />
              </div>

              <div>
                <label className={labelCls} htmlFor="description">What happened? <span className="text-red-500">*</span></label>
                <textarea
                  id="description"
                  className={inputCls}
                  rows={5}
                  value={form.description}
                  onChange={(e) => set('description', e.target.value)}
                  placeholder="Describe the hazard, incident or near miss in as much detail as you can"
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls} htmlFor="occurred_at">When did it occur?</label>
                  <input
                    id="occurred_at"
                    type="datetime-local"
                    className={inputCls}
                    value={form.occurred_at}
                    onChange={(e) => set('occurred_at', e.target.value)}
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="location">Location</label>
                  <input
                    id="location"
                    className={inputCls}
                    value={form.location}
                    onChange={(e) => set('location', e.target.value)}
                    placeholder="e.g. Packing shed, Block 4"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls} htmlFor="farm_id">Farm</label>
                  <select
                    id="farm_id"
                    className={inputCls}
                    value={form.farm_id}
                    onChange={(e) => set('farm_id', e.target.value)}
                  >
                    <option value="">— Not specified —</option>
                    {farms.map((f) => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls} htmlFor="severity">Severity</label>
                  <select
                    id="severity"
                    className={inputCls}
                    value={form.severity}
                    onChange={(e) => set('severity', e.target.value as ReportSeverity)}
                  >
                    {SEVERITIES.map((s) => (
                      <option key={s} value={s}>{SEVERITY_LABEL[s]}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-neutral-200 bg-white p-5 space-y-4">
              <h3 className="text-sm font-semibold text-neutral-900">Injury details (if any)</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls} htmlFor="injury_type">Injury type</label>
                  <input
                    id="injury_type"
                    className={inputCls}
                    value={form.injury_type}
                    onChange={(e) => set('injury_type', e.target.value)}
                    placeholder="e.g. Laceration, sprain"
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="body_part">Body part affected</label>
                  <input
                    id="body_part"
                    className={inputCls}
                    value={form.body_part}
                    onChange={(e) => set('body_part', e.target.value)}
                    placeholder="e.g. Left hand"
                  />
                </div>
              </div>
              <div>
                <label className={labelCls} htmlFor="treatment">Treatment given</label>
                <select
                  id="treatment"
                  className={inputCls}
                  value={form.treatment}
                  onChange={(e) => set('treatment', e.target.value as ReportTreatment)}
                >
                  {TREATMENTS.map((t) => (
                    <option key={t} value={t}>{TREATMENT_LABEL[t]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls} htmlFor="immediate_action">Immediate action taken</label>
                <textarea
                  id="immediate_action"
                  className={inputCls}
                  rows={3}
                  value={form.immediate_action}
                  onChange={(e) => set('immediate_action', e.target.value)}
                  placeholder="What was done straight away to make things safe?"
                />
              </div>
            </div>

            <div className="rounded-xl border border-neutral-200 bg-white p-5 space-y-4">
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                  checked={form.is_notifiable}
                  onChange={(e) => set('is_notifiable', e.target.checked)}
                />
                <span>
                  <span className="block text-sm font-medium text-neutral-900">This may be a notifiable event</span>
                  <span className="block text-xs text-neutral-500">
                    Serious injury/illness or dangerous incident notifiable to the WHS regulator
                  </span>
                </span>
              </label>

              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                  checked={form.is_anonymous}
                  onChange={(e) => set('is_anonymous', e.target.checked)}
                />
                <span>
                  <span className="block text-sm font-medium text-neutral-900">Submit anonymously</span>
                  <span className="block text-xs text-neutral-500">
                    Submit anonymously — your name will be hidden from managers. You can still track progress with the
                    code you receive.
                  </span>
                </span>
              </label>

              {!form.is_anonymous && (
                <div className="space-y-4 border-t border-neutral-100 pt-4">
                  <div className="flex items-center gap-2 text-sm text-neutral-600">
                    <ShieldQuestion className="h-4 w-4 text-neutral-400" />
                    Contact details (optional) — helps us follow up with you directly
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className={labelCls} htmlFor="contact_name">Name</label>
                      <input
                        id="contact_name"
                        className={inputCls}
                        value={form.contact_name}
                        onChange={(e) => set('contact_name', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="contact_email">Email</label>
                      <input
                        id="contact_email"
                        type="email"
                        className={inputCls}
                        value={form.contact_email}
                        onChange={(e) => set('contact_email', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="contact_phone">Phone</label>
                      <input
                        id="contact_phone"
                        className={inputCls}
                        value={form.contact_phone}
                        onChange={(e) => set('contact_phone', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <Button type="submit" isLoading={submitting} disabled={submitting}>
                Submit report
              </Button>
              <Button type="button" variant="secondary" onClick={() => navigate('/whs/track')}>
                Track an existing report
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
